import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import icons from './Icons';
import { GOOGLE_SHEET_URL, postToApi } from '../config';
import './contact.css';

const Contact = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    details: '',
    newsletter: false
  });

  // ── Spam Protection States ──
  const [honeypot, setHoneypot] = useState(''); // Invisible trap for bots
  const [captchaNums, setCaptchaNums] = useState({ num1: 3, num2: 4 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const mountTimeRef = useRef(Date.now()); // Time-gate to catch rapid bot submissions

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Generate random math challenge on mount
  useEffect(() => {
    mountTimeRef.current = Date.now();
    const n1 = Math.floor(Math.random() * 8) + 2;
    const n2 = Math.floor(Math.random() * 7) + 1;
    setCaptchaNums({ num1: n1, num2: n2 });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;
    // Phone: strip non-digits, cap at 10 characters
    if (name === 'phone') {
      finalValue = value.replace(/\D/g, '').slice(0, 10);
    }
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setCaptchaError('');

    // 1. Honeypot check: Bots fill hidden fields
    if (honeypot && honeypot.trim().length > 0) {
      // Silently pretend success to prevent bot adaptation, but DO NOT submit to sheet
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        if (onNavigate) onNavigate('thankyou');
        else window.location.hash = 'thankyou';
      }, 700);
      return;
    }

    // 2. Submission speed check: Real users take > 3 seconds to fill this form
    const elapsedSeconds = (Date.now() - mountTimeRef.current) / 1000;
    if (elapsedSeconds < 3) {
      setSubmitError('Please take a few seconds to review your inquiry details before submitting.');
      return;
    }

    // 3. Human verification math challenge
    const expectedSum = captchaNums.num1 + captchaNums.num2;
    if (parseInt(captchaAnswer.trim(), 10) !== expectedSum) {
      setCaptchaError(`Please solve the security check correctly (${captchaNums.num1} + ${captchaNums.num2} = ?).`);
      return;
    }

    // 4. Rate-limiting check: Prevent multiple submissions in rapid succession
    let lastSubmitTime = null;
    try {
      lastSubmitTime = localStorage.getItem('msquare_last_submit_ts');
    } catch (e) {
      // Storage unavailable: skip the client-side throttle
    }
    if (lastSubmitTime && Date.now() - parseInt(lastSubmitTime, 10) < 45000) {
      setSubmitError('You have already submitted an inquiry recently. Please wait a minute or contact us directly at +91 9870202444.');
      return;
    }

    // 5. Spam link filter: Reject messages with excess links or known spam patterns
    const linkMatches = (formData.details.match(/https?:\/\/|www\.|\[url=/gi) || []).length;
    if (linkMatches > 2) {
      setSubmitError('For security reasons, inquiries with multiple web links cannot be submitted.');
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setSubmitError('You appear to be offline. Please check your internet connection and try again.');
      return;
    }

    setLoading(true);

    const phone = formData.phone ? `+91${formData.phone}` : '';

    // 1. Fast path: our own API stores the inquiry instantly and delivers it to the
    //    Google Sheet in the background (with retries).
    const apiResult = await postToApi('/contact', {
      name: formData.name,
      company: formData.company,
      email: formData.email,
      phone,
      service: formData.service,
      budget: formData.budget,
      details: formData.details,
      newsletter: formData.newsletter,
      website: honeypot,
    }, { timeoutMs: 8000 });

    const apiData = apiResult.data || {};
    if (!(apiResult.ok && apiData.success)) {
      if (apiData.error && !apiData.fallback) {
        // Validation or rate-limit message from the server
        setLoading(false);
        setSubmitError(apiData.error);
        return;
      }

      // 2. Fallback (API unavailable / not configured): post straight to the Apps Script
      //    web app. Its response is opaque ('no-cors'); a rejected promise means the request
      //    never left the browser, so the visitor must not be told it was sent.
      if (!GOOGLE_SHEET_URL) {
        setLoading(false);
        setSubmitError('Contact form endpoint is not configured. Please contact info@msquareprofessionals.com.');
        return;
      }
      const payload = new URLSearchParams({
        timestamp: new Date().toISOString(),
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone,
        service: formData.service,
        budget: formData.budget,
        details: formData.details,
        newsletter: formData.newsletter ? 'Yes' : 'No'
      });
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null;
      try {
        await fetch(GOOGLE_SHEET_URL, {
          method: 'POST',
          mode: 'no-cors',
          keepalive: true,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: payload.toString(),
          signal: controller ? controller.signal : undefined,
        });
      } catch (err) {
        console.warn('Contact form delivery failed:', err);
        setLoading(false);
        setSubmitError('We could not send your inquiry. Please check your connection and try again, or email info@msquareprofessionals.com.');
        return;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    // Record last submit timestamp for client-side rate limiting
    try {
      localStorage.setItem('msquare_last_submit_ts', Date.now().toString());
    } catch (e) {
      // Ignore storage errors
    }

    // 2. Transition to the Thank You page
    setLoading(false);
    if (onNavigate) {
      onNavigate('thankyou');
    } else {
      window.location.hash = 'thankyou';
    }
  };

  return (
    <div className="contact-page">
      {/* ── Page Hero ── */}
      <section className="contact-hero text-center">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>
              <span className="badge-eyebrow mb-3">CONTACT US</span>
              <h1 className="contact-hero-title">
                Let's Discuss Your <span>Project.</span>
              </h1>
              <p className="contact-hero-subtitle">
                Share a few details about your business and goals. Our senior team will review your requirements and provide a preliminary scope and estimate within 24 hours.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Main Form & Info Grid ── */}
      <section className="contact-main-section py-5">
        <Container>
          <Row className="g-5">
            {/* Left Column: Benefits & Direct Info */}
            <Col xs={12} lg={5} className="d-flex flex-column justify-content-between">
              <div>
                <span className="badge-eyebrow mb-2">HOW WE WORK</span>
                <h2 className="fs-2 fw-bold mb-4">Direct Communication with Specialists</h2>
                <p className="text-secondary mb-4">
                  No junior account reps or scripted sales pitches. You talk directly with experienced specialists who handle campaign execution, workflow automation, and software engineering.
                </p>

                <div className="benefits-stack d-flex flex-column gap-3 mb-5">
                  <div className="benefit-card p-3 rounded-3 d-flex gap-3 align-items-center">
                    <div className="benefit-icon orange">{icons.clock}</div>
                    <div>
                      <strong className="d-block text-primary">Fast 24-Hour Response</strong>
                      <span className="text-secondary small">Clear assessment and project timeline delivered promptly.</span>
                    </div>
                  </div>

                  <div className="benefit-card p-3 rounded-3 d-flex gap-3 align-items-center">
                    <div className="benefit-icon blue">{icons.shield}</div>
                    <div>
                      <strong className="d-block text-primary">Transparent Scope & Pricing</strong>
                      <span className="text-secondary small">Zero hidden costs. Realistic milestones and deliverables.</span>
                    </div>
                  </div>

                  <div className="benefit-card p-3 rounded-3 d-flex gap-3 align-items-center">
                    <div className="benefit-icon purple">{icons.code}</div>
                    <div>
                      <strong className="d-block text-primary">Custom Solutions</strong>
                      <span className="text-secondary small">Tailored specifically to your business workflows and target market.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Direct channels card */}
              <div className="direct-channels-card p-4 rounded-4 mb-4">
                <h4 className="fs-6 fw-bold text-uppercase letter-spacing-1 mb-3 text-muted">DIRECT CHANNELS</h4>
                <div className="d-flex flex-column gap-3">
                  <a href="mailto:info@msquareprofessionals.com" className="direct-channel-item">
                    <span className="channel-icon">{icons.mail}</span>
                    <span>info@msquareprofessionals.com</span>
                  </a>
                  <a href="tel:+919870202444" className="direct-channel-item">
                    <span className="channel-icon">{icons.phone}</span>
                    <span>+91 9870202444</span>
                  </a>
                  <div className="direct-channel-item">
                    <span className="channel-icon">{icons.mapPin}</span>
                    <span>Sector 15, Gurugram, Haryana 122002</span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column: Interactive Lead Capture Form */}
            <Col xs={12} lg={7}>
              <Card className="contact-form-card p-4 p-md-5 rounded-4 border-0">
                <Card.Body className="p-0">
                  <div className="mb-4">
                    <span className="form-legend-tag font-monospace">PROJECT DETAILS</span>
                    <h2 className="fs-3 fw-bold mt-2 mb-1">Tell Us About Your Requirements</h2>
                    <p className="text-secondary small">Fields marked with an asterisk (*) are required.</p>
                  </div>

                  <Form onSubmit={handleSubmit} id="lead-form">
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactName">
                          <Form.Label className="small fw-semibold">Full Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            required
                            placeholder="e.g. Rahul Sharma"
                            value={formData.name}
                            onChange={handleChange}
                            className="custom-input"
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactCompany">
                          <Form.Label className="small fw-semibold">Company / Organization</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            placeholder="e.g. Acme Health Technologies"
                            value={formData.company}
                            onChange={handleChange}
                            className="custom-input"
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactEmail">
                          <Form.Label className="small fw-semibold">Business Email *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            required
                            placeholder="name@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            className="custom-input"
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactPhone">
                          <Form.Label className="small fw-semibold">Phone Number</Form.Label>
                          <div className="input-group">
                            <span
                              className="input-group-text custom-input fw-semibold"
                              style={{ borderRight: 'none', borderRadius: '8px 0 0 8px', color: 'var(--text-orange, #f97316)', userSelect: 'none' }}
                            >
                              +91
                            </span>
                            <Form.Control
                              type="tel"
                              name="phone"
                              placeholder="98700 00000"
                              value={formData.phone}
                              onChange={handleChange}
                              maxLength={10}
                              pattern="[0-9]{10}"
                              inputMode="numeric"
                              className="custom-input"
                              style={{ borderLeft: 'none', borderRadius: '0 8px 8px 0' }}
                            />
                          </div>
                          {formData.phone.length > 0 && formData.phone.length < 10 && (
                            <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                              Enter all 10 digits
                            </div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactService">
                          <Form.Label className="small fw-semibold">Service Area *</Form.Label>
                          <Form.Select
                            name="service"
                            required
                            value={formData.service}
                            onChange={handleChange}
                            className="custom-input"
                          >
                            <option value="">Select a primary service</option>
                            <option value="growth">Growth Marketing & Lead Generation</option>
                            <option value="automation">CRM & Business Automation</option>
                            <option value="software">Custom Software & Web Apps</option>
                            <option value="mobile">Mobile Application (iOS / Android)</option>
                            <option value="full">Full Growth Ecosystem (All 3 Pillars)</option>
                            <option value="consult">Strategy Consultation & System Audit</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group controlId="contactBudget">
                          <Form.Label className="small fw-semibold">Estimated Budget Range</Form.Label>
                          <Form.Select
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            className="custom-input"
                          >
                            <option value="">Select budget range</option>
                            <option value="under5">Under ₹5 Lakhs</option>
                            <option value="5-15">₹5 Lakhs – ₹15 Lakhs</option>
                            <option value="15-50">₹15 Lakhs – ₹50 Lakhs</option>
                            <option value="50plus">₹50 Lakhs+</option>
                            <option value="enterprise">Enterprise Contract</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group controlId="contactDetails">
                          <Form.Label className="small fw-semibold">Project Requirements & Timeline *</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="details"
                            required
                            placeholder="Tell us about your business goals, target timeline, or specific challenges you'd like us to solve..."
                            value={formData.details}
                            onChange={handleChange}
                            className="custom-input"
                          />
                        </Form.Group>
                      </Col>

                      {/* ── Invisible Honeypot Trap (Hidden from users, filled by bots) ── */}
                      <div
                        style={{
                          position: 'absolute',
                          left: '-9999px',
                          width: '1px',
                          height: '1px',
                          overflow: 'hidden',
                          opacity: 0,
                          pointerEvents: 'none'
                        }}
                        aria-hidden="true"
                      >
                        <label htmlFor="website_hp">Leave this field blank</label>
                        <input
                          type="text"
                          id="website_hp"
                          name="website_hp"
                          tabIndex="-1"
                          autoComplete="off"
                          value={honeypot}
                          onChange={(e) => setHoneypot(e.target.value)}
                        />
                      </div>

                      {/* ── Interactive Human Verification Check (Spam Stopper) ── */}
                      <Col xs={12} md={7}>
                        <Form.Group controlId="contactCaptcha">
                          <Form.Label className="small fw-semibold d-flex align-items-center gap-2">
                            <span>Security Check:</span>
                            <span className="badge bg-secondary-subtle text-secondary px-2 py-1 font-monospace">
                              {captchaNums.num1} + {captchaNums.num2} = ?
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>(human verification)</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            required
                            placeholder="Enter the sum"
                            value={captchaAnswer}
                            onChange={(e) => {
                              setCaptchaAnswer(e.target.value);
                              if (captchaError) setCaptchaError('');
                            }}
                            className="custom-input"
                            style={{ maxWidth: '200px' }}
                          />
                          {captchaError && (
                            <div className="text-danger small mt-1">
                              {captchaError}
                            </div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Check
                          type="checkbox"
                          id="contactNewsletter"
                          name="newsletter"
                          label="Keep me updated with practical digital growth insights from M Square."
                          checked={formData.newsletter}
                          onChange={handleChange}
                          className="text-secondary small mt-1"
                        />
                      </Col>

                      <Col xs={12} className="mt-4">
                        {submitError && (
                          <div className="alert alert-danger small mb-3" role="alert">
                            {submitError}
                          </div>
                        )}
                        <Button
                          type="submit"
                          disabled={loading}
                          className="btn-submit-brief w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                        >
                          <span>{loading ? 'Submitting Inquiry...' : 'Submit Project Inquiry'}</span>
                          {icons.arrowRight}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Headquarters Location & Map ── */}
      <section className="hq-section py-5 bg-secondary-alt">
        <Container>
          <div className="text-center mb-5">
            <span className="badge-eyebrow mb-2">OUR OFFICE</span>
            <h2 className="fs-2 fw-bold">Visit Us in Gurugram, India</h2>
            <p className="text-secondary">Centrally located in Sector 15, Gurugram.</p>
          </div>

          <Row className="g-4 align-items-center">
            <Col xs={12} lg={6}>
              <div className="hq-info-box p-4 rounded-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="hq-pin-circle">{icons.mapPin}</div>
                  <div>
                    <h4 className="fs-5 fw-bold m-0">Corporate Office</h4>
                    <span className="text-muted small">M Square Professionals Pvt. Ltd.</span>
                  </div>
                </div>

                <div className="hq-address-lines mb-4 text-secondary">
                  <p className="m-0 fw-semibold text-primary">SCO 40, 4th Floor, Civil Line</p>
                  <p className="m-0">Sector 15, Gurugram, Haryana 122002</p>
                  <p className="m-0 text-muted small mt-1">Timezone: IST (UTC +5:30)</p>
                </div>

                <div className="d-flex gap-3 flex-wrap">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=SCO+40+Sector+15+Gurugram"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-theme-outline d-inline-flex align-items-center gap-2"
                  >
                    <span>Open in Google Maps</span>
                    {icons.external}
                  </a>
                  <a
                    href="https://api.whatsapp.com/send/?phone=919870202444&text=Hi+MSquare!+I+would+like+to+inquire+about+services."
                    target="_blank"
                    rel="noreferrer"
                    className="btn-whatsapp-direct d-inline-flex align-items-center gap-2"
                  >
                    <span>Instant WhatsApp</span>
                  </a>
                </div>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="map-embed-wrapper rounded-4 overflow-hidden shadow-lg position-relative" style={{ minHeight: '320px', background: 'var(--bg-card)' }}>
                <iframe
                  title="MSquare Gurugram Location"
                  src="https://maps.google.com/maps?q=SCO%2040%20Sector%2015%20Gurugram&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="340"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Contact;
